import { ElementIconType } from "@/components/ui/icons";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import {
  FaroMenuItem,
  FaroTooltip,
  GaussianSplattingIcon,
} from "@faro-lotv/flat-ui";
import { generateGUID, type GUID } from "@faro-lotv/foundation";
import {
  IElementType,
  isIElementSectionDataSetCapture,
  type IElementGaussianSplats,
} from "@faro-lotv/ielement-types";
import {
  selectIElement,
  selectProjectId,
  selectRootId,
} from "@faro-lotv/project-source";
import {
  CoreFileUploader,
  MutationTypes,
  useApiClientContext,
  type BaseMutation,
  type CreateIElement,
  type MutationAddGaussianSplats,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type JSX,
} from "react";
import {
  ContextMenuActionType,
  type ContextMenuAction,
  type ContextMenuActionCustomComponentProps,
} from "../action-types";

export const UPLOAD_GAUSSIAN_SPLATS: ContextMenuAction = {
  type: ContextMenuActionType.uploadGaussianSplats,
  label: "Treat as georeferenced",
  icon: ElementIconType.UploadIcon,
  disabledMessageForNode: (iElement) => {
    if (!isIElementSectionDataSetCapture(iElement)) {
      return "Unsupported element.";
    }
  },
  tooltipMessage: () => "Import Gaussian Splats into your dataset",
  CustomComponent: GaussianSplatsUploader,
};

function GaussianSplatsUploader({
  elementId,
  dark,
  closeContextMenu,
}: ContextMenuActionCustomComponentProps): JSX.Element {
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const element = useAppSelector(selectIElement(elementId));
  const openDialog = useCallback(() => {
    inputRef?.click();
  }, [inputRef]);

  const disabledMessage =
    !element || !isIElementSectionDataSetCapture(element)
      ? "Unsupported element"
      : undefined;

  const [file, setFile] = useState<File>();
  const updateFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (!files?.length) {
        console.log("no files selected");
        return;
      }
      setFile(files[0]);
      closeContextMenu();
    },
    [closeContextMenu],
  );
  const projectId = useAppSelector(selectProjectId);
  const rootId = useAppSelector(selectRootId);

  const dispatch = useAppDispatch();

  const { coreApiClient, projectApiClient } = useApiClientContext();
  useEffect(() => {
    if (!file) return;
    if (!projectId) return;
    if (!rootId) return;

    const validFile = file;
    const validProjectId = projectId;
    const validRootId = rootId;

    const controller = new AbortController();

    async function uploadFile(): Promise<void> {
      const uriInfo: { url?: string; md5Hash?: string } = {};

      const uploader = new CoreFileUploader(
        validFile,
        validProjectId,
        coreApiClient,
      );
      try {
        await uploader.doUpload(controller.signal, (url, md5Hash) => {
          uriInfo.url = url;
          uriInfo.md5Hash = md5Hash;
          return Promise.resolve();
        });
      } catch (e) {
        console.error("Upload failed");
        console.error(e);
        return;
      }

      if (!uriInfo.url || !uriInfo.md5Hash) {
        console.error("Missing uri info");
        return;
      }

      const mutation = createGaussianSplatsMutation({
        file: validFile,
        rootId: validRootId,
        datasetId: elementId,
        uri: uriInfo.url,
        md5Hash: uriInfo.md5Hash,
      });

      try {
        await projectApiClient.applyMutations([
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- SDK lacks the capture-dataset variant.
          mutation as MutationAddGaussianSplats,
        ]);
      } catch (e) {
        console.error("Mutation failed");
        console.error(e);
      }
    }

    dispatch(setShowSpinner(true));

    uploadFile().finally(() => {
      setFile(undefined);
      dispatch(setShowSpinner(false));
    });
  }, [
    coreApiClient,
    dispatch,
    elementId,
    file,
    projectApiClient,
    projectId,
    rootId,
  ]);

  return (
    <FaroTooltip title={disabledMessage} placement="right">
      <Stack>
        <FaroMenuItem
          label="Upload gaussian splats"
          aria-label="Delete"
          dark={dark}
          Icon={GaussianSplattingIcon}
          onClick={openDialog}
          disabled={!!disabledMessage}
        />
        <input
          ref={setInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={updateFile}
          accept=".rad, .ply"
        />
      </Stack>
    </FaroTooltip>
  );
}

type MutationParams = {
  file: File;
  rootId: GUID;
  datasetId: GUID;
  uri: string;
  md5Hash: string;
};

type GaussianSplatsMutation = BaseMutation & {
  type: MutationTypes.MutationAddGaussianSplats;
  elementId: GUID;
  newElement: CreateIElement<IElementGaussianSplats>;
};

/** @returns A mutation adding a splat directly to a capture dataset without wrapper nodes. */
export function createGaussianSplatsMutation({
  file,
  rootId,
  datasetId,
  uri,
  md5Hash,
}: MutationParams): GaussianSplatsMutation {
  const timestamp = new Date().toISOString();

  return {
    type: MutationTypes.MutationAddGaussianSplats,
    id: generateGUID(),
    createdAt: timestamp,
    elementId: datasetId,
    newElement: {
      type: IElementType.gaussianSplats,
      id: generateGUID(),
      name: file.name,
      rootId,
      parentId: datasetId,
      uri,
      fileName: file.name,
      md5Hash,
      fileSize: file.size,
      createdAt: timestamp,
      modifiedAt: timestamp,
    },
  };
}
